import { RECAPTCHA_DEV, RECAPTCHA_DEV_SCORE } from '../constants/index';
import {
  logRecaptchaScriptLoadError,
  logRecaptchaExecuteError,
  logRecaptchaChallengeSuccess,
  logRecaptchaChallengeError,
} from '../helpers/faroLogger';

/**
 * Singleton service for managing Google reCAPTCHA Enterprise
 * Supports both v2 (checkbox) and v3 (score-based) versions
 */
class RecaptchaService {
  static instance = null;

  constructor() {
    if (RecaptchaService.instance) {
      return RecaptchaService.instance;
    }

    this.loadingPromise = null;
    this.useV3 = false; // Default to v2, will be set by GrowthBook
    this.listeners = new Map();
    RecaptchaService.instance = this;
  }

  static getInstance() {
    if (!RecaptchaService.instance) {
      RecaptchaService.instance = new RecaptchaService();
    }
    return RecaptchaService.instance;
  }

  /**
   * Subscribe to recaptcha events
   * @param {('loaded'|'versionChanged'|'error')} event - Event name
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribe(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        callbacks.delete(callback);
      }
    };
  }

  /**
   * Notify all subscribers of an event
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  notify(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => callback(data));
    }
  }

  /**
   * Set the reCAPTCHA version
   * @param {('v2'|'v3')} version - 'v2' for checkbox, 'v3' for score-based
   */
  setVersion(version) {
    const oldVersion = this.useV3;
    this.useV3 = version === 'v3';

    // Notify subscribers if version changed
    if (oldVersion !== this.useV3) {
      this.notify('versionChanged', { useV3: this.useV3 });
    }
  }

  /**
   * Clean up V2 widget DOM element and its rendered content
   */
  cleanupV2Widget() {
    const widget = this.getWidgetV2();
    if (widget) {
      // Clear all rendered content inside the widget
      widget.innerHTML = '';
    }
  }

  getSiteKey() {
    const inputId = this.useV3
      ? 'recaptcha-site-key-score'
      : 'recaptcha-site-key';
    const input = document.getElementById(inputId);
    const fallback = this.useV3 ? RECAPTCHA_DEV_SCORE : RECAPTCHA_DEV;

    return input?.value || fallback;
  }

  isLoaded() {
    return (
      window.grecaptcha &&
      window.grecaptcha.enterprise &&
      typeof window.grecaptcha.enterprise.execute === 'function'
    );
  }

  /**
   * Check if recaptcha is currently loading (for v2 only)
   * Returns true if v2 and script is still loading
   */
  isLoadingV2() {
    // For v3, never block (it's invisible)
    if (this.useV3) {
      return false;
    }

    // For v2, check if script is loaded
    return !this.isLoaded();
  }

  /**
   * Ensure reCAPTCHA script is loaded
   * Returns a promise that resolves when script is ready
   * Handles multiple simultaneous calls gracefully
   * Supports both v2 (checkbox) and v3 (score-based) versions
   */
  async ensureLoaded() {
    if (this.isLoaded()) {
      return Promise.resolve();
    }

    // If already loading, return existing promise
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Start loading
    this.loadingPromise = new Promise((resolve, reject) => {
      const siteKey = this.getSiteKey();

      const script = document.createElement('script');

      script.id = 'recaptcha-script';

      // v2: load without render parameter (will render manually)
      // v3: load with render parameter (invisible, score-based)
      script.src = this.useV3
        ? `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`
        : 'https://www.google.com/recaptcha/enterprise.js';

      script.onload = () => {
        window.grecaptcha.enterprise.ready(() => {
          this.loadingPromise = null;

          // Notify subscribers that recaptcha is loaded
          this.notify('loaded', { useV3: this.useV3 });

          // Keep backward compatibility with old event system
          if (window.recaptchaOnLoad) {
            window.recaptchaOnLoad();
          }

          resolve();
        });
      };

      script.onerror = (error) => {
        this.loadingPromise = null;

        const detailedError = new Error(
          `Failed to load reCAPTCHA script from ${script.src}. ` +
            `This may be due to: CORS/CSP policies, invalid site key, or domain restrictions.`,
        );
        detailedError.cause = error;

        logRecaptchaScriptLoadError(
          this.useV3 ? 'score' : 'checkbox',
          detailedError,
          siteKey,
        );

        // Notify subscribers about error
        this.notify('error', { error: detailedError, useV3: this.useV3 });

        reject(detailedError);
      };

      document.body.appendChild(script);
    });

    return this.loadingPromise;
  }

  async executeAction(action = 'submit') {
    await this.ensureLoaded();

    return new Promise((resolve, reject) => {
      window.grecaptcha.enterprise.ready(async () => {
        try {
          const siteKey = this.getSiteKey();
          const token = await window.grecaptcha.enterprise.execute(siteKey, {
            action,
          });
          resolve(token);
        } catch (error) {
          logRecaptchaExecuteError(action, error);
          reject(error);
        }
      });
    });
  }

  async challengeAccount(accountToken) {
    await this.ensureLoaded();

    return new Promise((resolve, reject) => {
      window.grecaptcha.enterprise.ready(async () => {
        try {
          const siteKey = this.getSiteKey();

          const token = await window.grecaptcha.enterprise.challengeAccount(
            siteKey,
            {
              'account-token': accountToken,
            },
          );

          logRecaptchaChallengeSuccess();

          resolve(token);
        } catch (error) {
          const errorResponse = {
            detail: 'Verification required to continue',
            status: 403,
          };

          logRecaptchaChallengeError(errorResponse, error);

          reject(errorResponse);
        }
      });
    });
  }

  /**
   * V2-specific: Get the checkbox widget element
   */
  getWidgetV2() {
    return document.querySelector('#recaptcha-checkbox');
  }

  /**
   * V2-specific: Render the checkbox widget
   * Should be called after ensureLoaded() completes
   */
  async renderV2() {
    if (!window.grecaptcha) {
      return;
    }

    await this.ensureLoaded();

    const widget = this.getWidgetV2();
    if (!widget) {
      return;
    }

    window.grecaptcha.enterprise.ready(() => {
      window.grecaptcha.enterprise.render(widget, {
        sitekey: this.getSiteKey(),
      });
    });
  }

  /**
   * V2-specific: Get the response from the checkbox widget
   * Returns null if widget is not available
   */
  getResponseV2() {
    const { grecaptcha } = window;

    if (!this.getWidgetV2() || !grecaptcha) {
      return null;
    }
    return grecaptcha.enterprise.getResponse();
  }

  /**
   * V2-specific: Reset the checkbox widget (clear selection)
   */
  resetV2() {
    const { grecaptcha } = window;

    if (!this.getWidgetV2() || !grecaptcha) {
      return;
    }
    grecaptcha.enterprise.reset();
  }

  async resetRecaptcha() {
    // V2: Only reset the checkbox widget, keep the script loaded
    if (!this.useV3) {
      this.resetV2();
      return;
    }

    // V3: Remove script and reload (needed for fresh tokens)
    const oldScript = document.getElementById('recaptcha-script');
    if (oldScript) {
      oldScript.remove();
    }

    if (window.grecaptcha) {
      delete window.grecaptcha;
    }

    this.loadingPromise = null;

    await this.ensureLoaded();
  }
}

export default RecaptchaService.getInstance();
